import { forwardRef, Fragment, useEffect, useImperativeHandle } from 'react';

import { SettingsStep } from 'pages/custom_tests/components/assign_wizard/steps/SettingsStep';
import { TargetsStep } from 'pages/custom_tests/components/assign_wizard/steps/TargetsStep';
import { ConfirmStep } from 'pages/custom_tests/components/assign_wizard/steps/ConfirmStep';
import { AssignWizardContext } from 'pages/custom_tests/components/assign_wizard/context';
import { SelectedContentReference, TransactionApi } from 'apis/TransactionApi';
import { addContentToTransactionChunked } from 'utils/addContentToTransaction';
import { GMetrixStepper } from 'components/GMetrixStepper/GMetrixStepper';
import { GMetrixDialog } from 'components/GMetrixDialog/GMetrixDialog';
import type { CustomTestRequiredLicense } from 'apis/CustomTestApi';
import { StudentGroupApi } from 'apis/StudentGroupApi';
import { filterOutNonExistentOrderIds } from 'utils';
import { useNotification } from 'app/notifications';
import { CustomTestApi } from 'apis/CustomTestApi';
import { useSuperState } from 'hooks';
import { useIntl } from 'react-intl';
import { useApp } from 'app';

export type AssignableItem = {
    CreatedDate: string;
    Description?: string;
    ExpirationDateString?: string;
    Expired?: boolean;
    Id: number;
    Identifier?: string;
    InstructorString?: string;
    Licenses?: string;
    Name?: string;
    OwnerUserId?: number;
    StudentCount?: number;
    UsesRemaining?: number;
};

export type AssignWizardShowOptions = {
    modeId?: number;
    onAssigned?: (targets: string[]) => void;
    testId: number;
    testName: string;
};

export type AssignWizardContextValue = {
    accessCodes: AssignableItem[];
    activeTab: 'classrooms' | 'accessCodes';
    classrooms: AssignableItem[];
    currentUserId: number;
    endDate: string;
    hasApplicationQuestions: boolean;
    initialAssignedAccessCodeIds: number[];
    initialAssignedClassroomIds: number[];
    loadError: string;
    loading: boolean;
    maxAttempts: string;
    modeId: number;
    myClassroomsOnly: boolean;
    proctoring: boolean;
    questionReview: boolean;
    requiredLicenses: CustomTestRequiredLicense[];
    retryLoad: () => void;
    searchTerm: string;
    selectedAccessCodes: AssignableItem[];
    selectedClassrooms: AssignableItem[];
    setActiveTab: (tab: 'classrooms' | 'accessCodes') => void;
    setEndDate: (value: string) => void;
    setMaxAttempts: (value: string) => void;
    setMyClassroomsOnly: (value: boolean) => void;
    setProctoring: (value: boolean) => void;
    setQuestionReview: (value: boolean) => void;
    setSearchTerm: (value: string) => void;
    setSelectedAccessCodes: (accessCodes: AssignableItem[]) => void;
    setSelectedClassrooms: (accessCodes: AssignableItem[]) => void;
    setStartDate: (value: string) => void;
    startDate: string;
    testId: number;
    testName: string;
    toggleAccessCode: (item: AssignableItem) => void;
    toggleClassroom: (item: AssignableItem) => void;
}

export type AssignWizardHandle = {
    hide: () => void;
    show: (options: AssignWizardShowOptions) => Promise<boolean>;
    visible: boolean;
};

enum EStep {
    TARGETS,
    SETTINGS,
    CONFIRM
}

export const AssignWizard = forwardRef<AssignWizardHandle, any>((_, forwardedRef) => {

    const [state, setState, resetState] = useSuperState({
        accessCodes: [] as AssignableItem[],
        activeTab: 'classrooms' as 'classrooms' | 'accessCodes',
        classrooms: [] as AssignableItem[],
        endDate: '',
        error: null as string,
        hasApplicationQuestions: false,
        initialAssignedAccessCodeIds: [] as number[],
        initialAssignedClassroomIds: [] as number[],
        loading: true,
        maxAttempts: '',
        modeId: 0,
        myClassroomsOnly: true,
        onAssigned: undefined as ((targets: string[]) => void) | undefined,
        proctoring: false,
        questionReview: false,
        reject: Function(),
        requiredLicenses: [] as CustomTestRequiredLicense[],
        resolve: Function(),
        searchTerm: '',
        selectedAccessCodes: [] as AssignableItem[],
        selectedClassrooms: [] as AssignableItem[],
        startDate: '',
        step: EStep.TARGETS as EStep,
        submitting: false,
        testId: 0,
        testName: '',
        validationError: '',
        visible: false
    });

    const { accessCodes, activeTab, classrooms, endDate, loading, maxAttempts, modeId, myClassroomsOnly, proctoring, questionReview,
        resolve, searchTerm, selectedAccessCodes, selectedClassrooms, startDate, testId, testName, visible } = state;

    const { selectedPartner, userId } = useApp();
    const { formatMessage } = useIntl();
    const { createToast } = useNotification();

    const stepItems = [
        { label: formatMessage({ id: 'select_targets.fd423028', defaultMessage: 'Select Targets' }) },
        { label: formatMessage({ id: 'settings.277e0df0', defaultMessage: 'Settings' }) },
        { label: formatMessage({ id: 'confirm.297ab8f4', defaultMessage: 'Confirm' }) }
    ];

    const setSelectedClassrooms = (selectedClassrooms: AssignableItem[]) => setState({ selectedClassrooms });

    const setSelectedAccessCodes = (selectedAccessCodes: AssignableItem[]) => setState({ selectedAccessCodes });

    // Single-select for the *new* target: a user may add one classroom OR one access code at a
    // time. Already-assigned targets (seeded on load) are preserved and never overridden, so a
    // toggle keeps the locked set and swaps only the newly picked item.
    const toggleClassroom = (item: AssignableItem) => setState(prev => {

        const isCurrentPick = prev.selectedClassrooms.some(({ Id }) => Id === item.Id);
        const lockedClassrooms = prev.selectedClassrooms.filter(({ Id }) => prev.initialAssignedClassroomIds.includes(Id));
        const lockedAccessCodes = prev.selectedAccessCodes.filter(({ Id }) => prev.initialAssignedAccessCodeIds.includes(Id));

        return {
            selectedAccessCodes: lockedAccessCodes,
            selectedClassrooms: isCurrentPick ? lockedClassrooms : [...lockedClassrooms, item],
            validationError: ''
        };

    });

    const toggleAccessCode = (item: AssignableItem) => setState(prev => {

        const isCurrentPick = prev.selectedAccessCodes.some(({ Id }) => Id === item.Id);
        const lockedClassrooms = prev.selectedClassrooms.filter(({ Id }) => prev.initialAssignedClassroomIds.includes(Id));
        const lockedAccessCodes = prev.selectedAccessCodes.filter(({ Id }) => prev.initialAssignedAccessCodeIds.includes(Id));

        return {
            selectedAccessCodes: isCurrentPick ? lockedAccessCodes : [...lockedAccessCodes, item],
            selectedClassrooms: lockedClassrooms,
            validationError: ''
        };

    });

    const show = (options: AssignWizardShowOptions) => new Promise<boolean>((resolve, reject) => {

        const { modeId, onAssigned, testId, testName } = options;

        setState({
            modeId: modeId ?? 0,
            onAssigned,
            reject,
            resolve,
            testId,
            testName,
            visible: true
        });

    });

    const hide = () => {

        resolve(false);
        resetState();

    };

    const handleClose = () => {

        resolve(false);
        resetState();

    };

    const validateCurrentStep = (): boolean => {

        if (state.step === EStep.TARGETS && state.selectedAccessCodes.length === 0 && state.selectedClassrooms.length === 0) {

            setState({ validationError: formatMessage({ id: 'please_select_at_least_one_access_code_o.646b9f03', defaultMessage: 'Please select at least one access code or classroom.' }) });
            return false;

        }

        if (state.step === EStep.SETTINGS && state.startDate && state.endDate && state.endDate < state.startDate) {

            setState({ validationError: formatMessage({ id: 'end_date_must_be_on_or_after_the_start_d.72bf59a7', defaultMessage: 'End date must be on or after the start date.' }) });
            return false;

        }

        setState({ validationError: '' });
        return true;

    };

    const handleNext = () => {

        if (!validateCurrentStep()) return;

        setState({
            step: state.step === EStep.TARGETS ? EStep.SETTINGS : EStep.CONFIRM,
            validationError: ''
        });

    };

    const handleBack = () => setState({
        step: state.step === EStep.CONFIRM ? EStep.SETTINGS : EStep.TARGETS,
        validationError: ''
    });

    // Resolves the custom test's content reference (id + the license order it should consume).
    // A custom test's content deliverable Id equals its TestId, so match on Id + IsCustomTest.
    const buildTestContentRef = async (): Promise<SelectedContentReference> => {

        const [content, licenseData] = await Promise.all([
            TransactionApi.GetAllContentForTransactions(false, true, selectedPartner.id),
            TransactionApi.GetAllLicenses(selectedPartner.id)
        ]);

        const testContent = content.contentFlatList.find(c => c.IsCustomTest && c.Id === state.testId);
        const orderIds = testContent?.OrderIds ?? [];
        const restrictedOrderId = filterOutNonExistentOrderIds(orderIds, licenseData.licenses)[0] ?? orderIds[0] ?? 0;

        return { id: state.testId, restrictedOrderId };

    };

    // Adds the custom test as real content to every NEWLY assigned access code / classroom so
    // students can start it. Targets already assigned when the wizard opened are skipped (they
    // already have the content). Returns the display names of any targets whose content add failed.
    const assignTestContentToNewItems = async (): Promise<string[]> => {

        const newAccessCodes = state.selectedAccessCodes.filter(({ Id }) => !state.initialAssignedAccessCodeIds.includes(Id));
        const newClassrooms = state.selectedClassrooms.filter(({ Id }) => !state.initialAssignedClassroomIds.includes(Id));

        if (newAccessCodes.length === 0 && newClassrooms.length === 0) return [];

        let testContentRef: SelectedContentReference;

        try {

            testContentRef = await buildTestContentRef();

        } catch (e) {

            return [
                ...newAccessCodes.map(ac => ac.Identifier ?? ac.Name ?? String(ac.Id)),
                ...newClassrooms.map(c => c.Name ?? String(c.Id))
            ];

        }

        const failed: string[] = [];

        for (const accessCode of newAccessCodes) {

            try {

                await addContentToTransactionChunked(accessCode.Id, [testContentRef], [], []);

            } catch (e) {

                failed.push(accessCode.Identifier ?? accessCode.Name ?? String(accessCode.Id));

            }

        }

        for (const classroom of newClassrooms) {

            try {

                const transactions = await StudentGroupApi.GetTransactionsFromGroup(classroom.Id, selectedPartner.id);
                const transactionId = transactions?.[0]?.Id;

                if (!transactionId) {

                    failed.push(classroom.Name ?? String(classroom.Id));
                    continue;

                }

                await addContentToTransactionChunked(transactionId, [testContentRef], [], []);

            } catch (e) {

                failed.push(classroom.Name ?? String(classroom.Id));

            }

        }

        return failed;

    };

    const handleSubmit = async () => {

        try {

            setState({ submitting: true });

            const transactionIds = state.selectedAccessCodes.map(t => t.Id);
            const groupIds = state.selectedClassrooms.map(c => c.Id);

            // Settings apply to NEWLY added targets only — kept targets preserve their previously
            // configured settings server-side. Dates are hidden for access-code targets (codes
            // carry their own expiration), so they are omitted from the payload in that case.
            const isAccessCodeTarget = state.selectedAccessCodes.some(({ Id }) => !state.initialAssignedAccessCodeIds.includes(Id));
            const maxAttempts = Number(state.maxAttempts);

            const result = await CustomTestApi.SaveAssignedTransactionsAndGroups(state.testId, selectedPartner.id, transactionIds, groupIds, {
                allowReview: state.questionReview,
                isProctored: state.proctoring,
                ...(maxAttempts > 0 ? { maxAttempts } : {}),
                ...(!isAccessCodeTarget && state.startDate ? { startDate: state.startDate } : {}),
                ...(!isAccessCodeTarget && state.endDate ? { dueDate: state.endDate } : {})
            });

            if (!result?.Done) {

                createToast(
                    result?.Message || formatMessage({ id: 'failed_to_save_assignments.3fd63746', defaultMessage: 'Failed to save assignments.' }),
                    'danger'
                );
                setState({ submitting: false });
                return;

            }

            // The assignment rows exist now, but the test still has to be added as content to each
            // newly assigned target's transaction or students can't start it.
            const failedContent = await assignTestContentToNewItems();

            if (failedContent.length > 0) {

                createToast(
                    formatMessage({ id: 'assignments_saved_but_the_test_could_not.b15cf146', defaultMessage: 'Assignments saved, but the test could not be added as content to: ' }) + failedContent.join(', '),
                    'warning'
                );

            }

            // Report only the targets added in this pass (not ones already assigned before the
            // wizard opened) so the success screen can log each assignment as its own entry.
            const newlyAssignedNames = [
                ...state.selectedClassrooms
                    .filter(c => !state.initialAssignedClassroomIds.includes(c.Id))
                    .map(c => c.Name),
                ...state.selectedAccessCodes
                    .filter(a => !state.initialAssignedAccessCodeIds.includes(a.Id))
                    .map(a => a.Name || a.Identifier)
            ].filter(Boolean) as string[];

            state.onAssigned?.(newlyAssignedNames);

            resolve(true);
            resetState();

        } catch (e: any) {

            // ApiCaller rethrows the serialized .NET exception, whose text is on `Message`.
            createToast(
                e?.Message || e?.message || formatMessage({ id: 'failed_to_save_assignments.3fd63746', defaultMessage: 'Failed to save assignments.' }),
                'danger'
            );
            setState({ submitting: false });

        }

    };

    const handleActionClick = () => state.step === EStep.CONFIRM ? handleSubmit() : handleNext();

    const getActionButtonText = (): string => {

        switch (state.step){

            case EStep.TARGETS:
                return `${formatMessage({ id: 'next.50b7834e', defaultMessage: 'Next' })}: ${formatMessage({ id: 'settings.277e0df0', defaultMessage: 'Settings' })}`;
            case EStep.SETTINGS:
                return `${formatMessage({ id: 'next.50b7834e', defaultMessage: 'Next' })}: ${formatMessage({ id: 'confirm.297ab8f4', defaultMessage: 'Confirm' })}`;
            case EStep.CONFIRM:
                return state.submitting
                    ? (formatMessage({ id: 'assigning.8cc92c66', defaultMessage: 'Assigning...' }))
                    : (formatMessage({ id: 'confirm_assignment.f092be05', defaultMessage: 'Confirm Assignment' }));
            default:
                return '';

        }

    };

    const getCloseButtonText = (): string => {

        switch (state.step){

            case EStep.TARGETS:
                return formatMessage({ id: 'cancel.89a86adc', defaultMessage: 'Cancel' });
            case EStep.SETTINGS:
                return `${formatMessage({ id: 'previous.f825144b', defaultMessage: 'Previous' })}: ${formatMessage({ id: 'targets.42d7d147', defaultMessage: 'Targets' })}`;
            case EStep.CONFIRM:
                return `${formatMessage({ id: 'previous.f825144b', defaultMessage: 'Previous' })}: ${formatMessage({ id: 'settings.277e0df0', defaultMessage: 'Settings' })}`;
            default:
                return '';

        }

    };

    // The non-paged endpoints serialize a missing expiration as 0001-01-01, so a date only
    // counts as real (and can mark the target expired) when its year is past the epoch.
    const hasRealExpiration = (value?: Date | string | null) => {

        if (!value) return false;
        const parsed = new Date(value);
        return !Number.isNaN(parsed.getTime()) && parsed.getFullYear() > 1970;

    };

    const fetchData = async () => {

        try {

            const [transactions, groups, assignedInfo] = await Promise.all([
                TransactionApi.GetAll(),
                StudentGroupApi.GetAll('Partner'),
                CustomTestApi.GetAssignedTransactionsAndGroups(testId, selectedPartner.id)
            ]);

            const accessCodes: AssignableItem[] = transactions.map(t => ({
                CreatedDate: t.CreatedDateString,
                ExpirationDateString: t.ExpirationDateString ?? '',
                Expired: !!t.ExpirationDate && hasRealExpiration(t.ExpirationDate) && new Date(t.ExpirationDate).getTime() < Date.now(),
                Id: t.Id,
                Identifier: t.Identifier,
                Licenses: t.LicensesCSV || t.LicenseName || '',
                Name: t.Name,
                StudentCount: t.NumOfStudents ?? 0,
                UsesRemaining: t.UsesRemaining ?? 0
            }));

            // Archived classrooms stay in the list when they are already assigned to this
            // test — dropping them would omit the assignment from the submitted list and
            // the save proc would destroy it. They render as locked rows.
            const classrooms: AssignableItem[] = groups
                .filter(g => !g.Archived || assignedInfo.AssignedStudentGroupIds.includes(g.Id))
                .map(g => ({
                    CreatedDate: g.DateCreate,
                    Description: g.Description ?? '',
                    Expired: !!g.ExpirationDate && hasRealExpiration(g.ExpirationDate) && new Date(g.ExpirationDate).getTime() < Date.now(),
                    Id: g.Id,
                    Identifier: g.Control ?? '',

                    // StudentGroup.Instructors arrives as a display STRING from the server despite
                    // the declared Instructor[] type — use it verbatim, never .map over it.
                    InstructorString: g.InstructorString || (typeof g.Instructors === 'string' ? g.Instructors : ''),
                    Name: g.Name,
                    OwnerUserId: g.UserId,
                    StudentCount: g.StudentCount ?? 0
                }));

            const initialSelectedAccessCodes = accessCodes.filter(({ Id }) => assignedInfo.AssignedTransactionIds.includes(Id));
            const initialSelectedClassrooms = classrooms.filter(({ Id }) => assignedInfo.AssignedStudentGroupIds.includes(Id));

            setState({
                accessCodes,
                classrooms,
                initialAssignedAccessCodeIds: initialSelectedAccessCodes.map(({ Id }) => Id),
                initialAssignedClassroomIds: initialSelectedClassrooms.map(({ Id }) => Id),
                loading: false,
                selectedAccessCodes: initialSelectedAccessCodes,
                selectedClassrooms: initialSelectedClassrooms
            });

        } catch (e){

            setState({
                error: e?.message || (formatMessage({ id: 'failed_to_load_assignment_targets.97a83181', defaultMessage: 'Failed to load assignment targets.' })),
                loading: false
            });

        }

    };

    // Advisory data for the Confirm step (SMS notice) and the access-code license warnings.
    // Failures here never block the wizard — the hints are simply not rendered.
    const fetchTestInfo = async () => {

        try {

            const [detail, requiredLicenses] = await Promise.all([
                CustomTestApi.Get(testId),
                CustomTestApi.GetRequiredLicenses(testId)
            ]);

            setState(prev => ({
                hasApplicationQuestions: !!detail.HasApplicationQuestions,

                // Callers usually pass modeId to show(); fall back to the fetched detail so
                // the Confirm step never mislabels a Both (3) test.
                modeId: prev.modeId || detail.ModeId,
                requiredLicenses
            }));

        } catch {

            // Intentionally ignored — advisory only.

        }

    };

    const retryLoad = () => {

        setState({
            error: null,
            loading: true
        });
        fetchData();

    };

    const handleChangeStep = e => e.value <= state.step && setState({ step: e.value, validationError: '' });

    useImperativeHandle(forwardedRef, () => ({
        hide,
        show,
        visible
    }));

    // fetchData is intentionally omitted from deps — runs once on mount
    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {

        if (!visible) return;

        fetchData();
        fetchTestInfo();

    }, [visible]);
    /* eslint-enable react-hooks/exhaustive-deps */

    const context: AssignWizardContextValue = {
        accessCodes,
        activeTab,
        classrooms,
        currentUserId: userId,
        endDate,
        hasApplicationQuestions: state.hasApplicationQuestions,
        initialAssignedAccessCodeIds: state.initialAssignedAccessCodeIds,
        initialAssignedClassroomIds: state.initialAssignedClassroomIds,
        loadError: state.error ?? '',
        loading,
        maxAttempts,
        modeId,
        myClassroomsOnly,
        proctoring,
        questionReview,
        requiredLicenses: state.requiredLicenses,
        retryLoad,
        searchTerm,
        selectedAccessCodes,
        selectedClassrooms,
        setActiveTab: (tab: 'classrooms' | 'accessCodes') => setState({ activeTab: tab }),
        setEndDate: (value: string) => setState({ endDate: value }),
        setMaxAttempts: (value: string) => setState({ maxAttempts: value }),
        setMyClassroomsOnly: (value: boolean) => setState({ myClassroomsOnly: value }),
        setProctoring: (value: boolean) => setState({ proctoring: value }),
        setQuestionReview: (value: boolean) => setState({ questionReview: value }),
        setSearchTerm: (value: string) => setState({ searchTerm: value }),
        setSelectedAccessCodes,
        setSelectedClassrooms,
        setStartDate: (value: string) => setState({ startDate: value }),
        startDate,
        testId,
        testName,
        toggleAccessCode,
        toggleClassroom
    };

    const Steps = [TargetsStep, SettingsStep, ConfirmStep];
    const StepComponent = Steps?.[state.step] || Fragment;

    // Confirm is a no-op when the only selected targets were already assigned before the
    // wizard opened — the button stays disabled until a NEW target is picked.
    const hasNewSelection = selectedClassrooms.some(({ Id }) => !state.initialAssignedClassroomIds.includes(Id))
        || selectedAccessCodes.some(({ Id }) => !state.initialAssignedAccessCodeIds.includes(Id));

    return (
        <AssignWizardContext.Provider value={context}>
            <GMetrixDialog
                actionButtonClick={handleActionClick}
                actionButtonDisabled={state.submitting || (state.step === EStep.CONFIRM && !hasNewSelection)}
                actionButtonText={getActionButtonText()}
                closeButtonText={getCloseButtonText()}
                dialogLabel={formatMessage({ id: 'assign_test.5de97fef', defaultMessage: 'Assign Test' })}
                onCancel={state.step === EStep.TARGETS ? handleClose : handleBack}
                onClose={handleClose}
                show={state.visible}
                size="xl"
            >
                <div className="my-4">
                    <GMetrixStepper
                        handleChange={handleChangeStep}
                        linear={false}
                        setStep={(step: EStep) => setState({ step })}
                        step={state.step}
                        stepItems={stepItems}
                    />
                </div>

                {state.validationError && (
                <div className="alert alert-danger">
                    {state.validationError}
                </div>
                )}

                <StepComponent />

            </GMetrixDialog>

        </AssignWizardContext.Provider>
    );

});

AssignWizard.displayName = 'AssignWizard';
